import { supabase } from './supabaseClient';
import type { Note, Quote, QuoteOrderLine, QuoteOrderTranche, Report, ServiceItem } from '../types';
import { loadProjectById, saveProjectToCloud } from './cloudService';
import { mapQuoteRow, QuoteConflictError, QuoteNotSavedError } from './crmService';
import {
    buildTranche,
    formatEur,
    isTrancheInOrderValue,
    markTrancheApplied,
    normalizeOrderLines,
    orderValueCentsToWrite,
    orderValueNoteFor,
    planTrancheForProjectLv,
    statusAfterLines,
    trancheNoteText,
    validateOrderLines,
    type LvPlan,
} from './quoteOrderTranches';
import { applyFlatInclusionDecisions, pendingFlatInclusionDecisions, type FlatInclusionDecision } from './flatRateLogic';
import { fetchOrderValue, fixiereAuftragswertBestEffort } from './erpSteeringService';
import { isProjectArchived } from './projectBilling';
import { isMissingOrderTranchesSchema, MISSING_ORDER_TRANCHES_MESSAGE } from './quoteOrderSchema';
import { describeError } from './errorMessage';
import {
    isSupplementQuote,
    planSupplementForProjectLv,
    priorSupplementServiceIds,
    proposeSupplementMapping,
    supplementNoteText,
} from './quoteSupplementLogic';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TEILBEAUFTRAGUNG — Rest beauftragen / Rest entfällt
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ablauf für einen späteren Auftragsteil (Rechenkern: quoteOrderTranches.ts):
 *
 *  1. Teil am Angebot eintragen — an `updated_at` verankert (wie die
 *     Annahme). Status wird `accepted`, sobald nichts mehr offen ist.
 *  2. Nur `order`: Projekt frisch laden, Vertragsmengen erhöhen bzw.
 *     Positionen anhängen, eine Projektnotiz mit der id des Teils anlegen und
 *     über saveProjectToCloud speichern (Konflikt-Anker, Kindtabellen) —
 *     derselbe Weg wie der Nachtragsdialog (projectSupplement.ts).
 *  3. Teil am Angebot als eingetragen markieren (`appliedAt`).
 *
 * Scheitert 2 oder 3, bleibt der Teil am Angebot „ausstehend" und
 * `applyTrancheToProject` holt ihn nach. WIEDERHOLBAR, weil die Notiz die id
 * des Teils trägt: steht sie im Projekt, ist das LV schon angepasst — dann
 * wird nur noch markiert, nie ein zweites Mal erhöht.
 *
 * NACHTRAGSANGEBOT (details.nachtragZu, quoteSupplementLogic): dieselben drei
 * Schritte, aber mit dem Nachtrags-Plan — die Positionen tragen eigene ids
 * und finden ihre Projektposition über die Zuordnung am Auftragsteil
 * (`serviceIdByItemId`). Ein neuer Teil speichert seine Zuordnung mit, damit
 * „Erneut eintragen" genau dasselbe tut (supplementTranchePlan).
 */

/** Die Annahme legte Teil 1 an — spätere Teile nur bei `partial`. */
const assertPartial = (q: Quote) => {
    if (q.status !== 'partial') {
        throw new Error('Nur ein teilbeauftragtes Angebot hat einen offenen Rest.');
    }
};

export interface RecordTrancheInput {
    kind: QuoteOrderTranche['kind'];
    lines: QuoteOrderLine[];
    orderDate?: string;
    customerRef?: string;
    note?: string;
    actor: { id?: string; name: string };
    /** Nur `order`: Monatspauschale ja/nein je NEU angehängter Position. */
    flatDecisions?: Partial<Record<string, FlatInclusionDecision>>;
    /** Nur `order`: neues Projektende (yyyy-mm-dd); leer = unverändert. */
    endDate?: string;
    /** Nur `order`: Auftragswert NEU in Cent (bisher + dieser Teil); 0/leer = nicht ändern. */
    orderValueCents?: number;
}

export interface RecordTrancheResult {
    /** Angebot nach dem letzten erfolgreichen Schreiben — der neue Anker des Editors. */
    quote: Quote;
    tranche: QuoteOrderTranche;
    /** Steht der Teil im Projekt-LV? (`decline`: immer true) */
    applied: boolean;
    /** Warum das Eintragen ins Projekt scheiterte — der Teil ist trotzdem erfasst. */
    applyError?: string;
}

/**
 * Neuen Auftragsteil erfassen (Rest beauftragen oder Rest entfällt).
 * Wirft, wenn NICHTS geschrieben wurde (Prüfung, Konflikt, fehlende Migration).
 * Ist der Teil am Angebot, aber das Projekt noch nicht angepasst, kommt das
 * als `applied: false` zurück.
 */
export const recordQuoteTranche = async (base: Quote, input: RecordTrancheInput): Promise<RecordTrancheResult> => {
    assertPartial(base);
    if (input.kind === 'order' && !base.projectId) {
        throw new Error('Am Angebot hängt kein Projekt — der Rest kann nicht eingetragen werden.');
    }
    if (input.kind === 'decline' && !input.note?.trim()) {
        throw new Error('Bitte begründen, warum der Rest entfällt.');
    }
    const lines = normalizeOrderLines(input.lines);
    const errors = validateOrderLines(base, lines);
    if (errors.length) throw new Error(errors.join(' '));

    // Paketposition ueber Menge 1 (paketErhoehungGesperrt): VOR dem ersten
    // Schreiben pruefen. Danach waere der Teil am Angebot erfasst, das
    // Eintragen ins Projekt scheiterte bei jedem Versuch, und das Angebot
    // stuende dauerhaft mit einem „ausstehenden" Teil da. Gegen den frischen
    // Projektstand — der Dialog prueft nur gegen den Stand beim Oeffnen.
    // Fehlt das Projekt, faellt das unten beim Eintragen auf (wie bisher).
    // Nachtrag: derselbe Zeitpunkt rechnet die Zuordnung, die der Teil
    // mitbekommt — fehlende Zielpositionen sperren hier, nicht erst danach.
    let supplementIds: Record<string, string> | undefined;
    if (input.kind === 'order' && base.projectId) {
        const project = (await loadProjectById(base.projectId)) as LoadedProject | null;
        const services: ServiceItem[] = Array.isArray(project?.data?.metadata?.globalData?.services)
            ? project!.data.metadata.globalData.services
            : [];
        if (isSupplementQuote(base)) {
            if (!project) throw new Error('Das Projekt zum Nachtrag wurde nicht gefunden (gelöscht?).');
            const plan = supplementTranchePlan(base, services, lines);
            assertPlanUsable(plan);
            supplementIds = plan.serviceIdByItemId;
        } else {
            const plan = planTrancheForProjectLv(services, base.items || [], lines);
            if (plan.packageBlocks.length) throw new Error(plan.packageBlocks.map((b) => b.message).join(' '));
        }
    }

    const now = new Date().toISOString();
    const built = buildTranche(base, {
        id: crypto.randomUUID(),
        at: now,
        kind: input.kind,
        lines,
        orderDate: input.orderDate,
        by: input.actor.id,
        byName: input.actor.name,
        customerRef: input.customerRef,
        note: input.note,
    });
    const tranche: QuoteOrderTranche = supplementIds && Object.keys(supplementIds).length > 0
        ? { ...built, serviceIdByItemId: supplementIds }
        : built;
    const nextStatus = statusAfterLines(base, lines);

    // 1) Teil am Angebot — verankert auf dem Stand, den der Nutzer sieht.
    //    Zwei parallele „Rest beauftragen" koennen so nicht beide durchgehen.
    const { data: rows, error } = await supabase
        .from('quotes')
        .update({
            order_tranches: [...(base.orderTranches || []), tranche],
            status: nextStatus,
            updated_at: now,
        })
        .eq('id', base.id)
        .eq('status', 'partial')
        .eq('updated_at', base.updatedAt)
        .select('*, customers(name)');
    if (error) {
        if (isMissingOrderTranchesSchema(error)) throw new Error(MISSING_ORDER_TRANCHES_MESSAGE);
        throw error;
    }
    if (!rows?.length) {
        const { data: current, error: readErr } = await supabase
            .from('quotes').select('*, customers(name)').eq('id', base.id).maybeSingle();
        if (readErr) throw readErr;
        if (!current) throw new QuoteNotSavedError();
        throw new QuoteConflictError(mapQuoteRow(current));
    }
    const recorded = mapQuoteRow(rows[0]);
    if (input.kind === 'decline') return { quote: recorded, tranche, applied: true };

    try {
        const applied = await applyTrancheToProject(recorded, tranche.id, {
            actorName: input.actor.name,
            flatDecisions: input.flatDecisions,
            endDate: input.endDate,
            orderValueCents: input.orderValueCents,
        });
        return { quote: applied, tranche, applied: true };
    } catch (e) {
        console.error('[quoteOrderService] Auftragsteil erfasst, Projekt-LV nicht angepasst', e);
        return { quote: recorded, tranche, applied: false, applyError: describeError(e) };
    }
};

/** Projekt, soweit das Eintragen es braucht (loadProjectById). */
export interface LoadedProject {
    id: string;
    user_id?: string;
    status?: string | null;
    parent_project_id?: string | null;
    customer_id?: string | null;
    project_number?: string | null;
    name?: string | null;
    data: Report;
}

/** Was assertProjectWritable vom Projekt liest — auch eine Zeile der Projektsuche. */
export interface WritableProjectLike {
    status?: string | null;
    parent_project_id?: string | null;
    data?: {
        metadata?: {
            globalData?: {
                status?: string | null;
                linkedProjectConfig?: { role?: string; linkedProjectName?: string } | null;
            } | null;
        } | null;
    } | null;
}

/** Was der Dialog vom Projekt braucht, einmal beim Öffnen geladen. */
export interface TrancheContext {
    /** Leistungspositionen des Projekts — Grundlage der Vorschau (planTrancheForProjectLv). */
    services: ServiceItem[];
    endDate?: string;
    projectNumber?: string;
    projectName?: string;
    /** Warum nicht eingetragen werden kann (Archiv, Tochter) — sperrt „Rest beauftragen". */
    blocker?: string;
    /** Bisher fixierter Auftragswert (Cent), null = keiner / nicht lesbar. */
    orderValueCents: number | null;
    /** Vermerk dazu — trägt die Kennung des zuletzt eingerechneten Teils. */
    orderValueNote?: string;
}

/** Projekt laden und prüfen — ohne zu schreiben. Wirft, wenn es fehlt. */
export const loadTrancheContext = async (quote: Quote): Promise<TrancheContext> => {
    if (!quote.projectId) throw new Error('Am Angebot hängt kein Projekt.');
    const project = (await loadProjectById(quote.projectId)) as LoadedProject | null;
    if (!project) throw new Error('Das Projekt zum Angebot wurde nicht gefunden (gelöscht?).');
    let blocker: string | undefined;
    try { assertProjectWritable(project); } catch (e) { blocker = (e as Error).message; }
    const ov = await fetchOrderValue(quote.projectId);
    const meta = project.data?.metadata;
    return {
        services: Array.isArray(meta?.globalData?.services) ? meta.globalData.services : [],
        endDate: meta?.globalData?.endDate,
        projectNumber: meta?.projectNumber,
        projectName: meta?.projectName,
        blocker,
        orderValueCents: ov ? ov.orderValueCents : null,
        orderValueNote: ov?.note,
    };
};

/**
 * Vorschau eines Teils gegen den geladenen Projektstand (rein).
 * `tranche`: beim erneuten Eintragen der erfasste Teil — beim Nachtrag gilt
 * dann seine gespeicherte Zuordnung statt des Vorschlags.
 */
export const previewTranche = (
    ctx: TrancheContext,
    quote: Quote,
    lines: QuoteOrderLine[],
    tranche?: QuoteOrderTranche,
): { plan: LvPlan; flatDecisionIds: string[] } => {
    const plan = isSupplementQuote(quote)
        ? supplementTranchePlan(quote, ctx.services, lines, tranche)
        : planTrancheForProjectLv(ctx.services, quote.items || [], lines);
    return { plan, flatDecisionIds: pendingFlatInclusionDecisions(plan.services, plan.newServiceIds, {}) };
};

/** Nachtragsnummer des Angebots (LV-Präfix „N<seq>.") — ohne Angabe 1. */
const supplementSeqOf = (q: Pick<Quote, 'details'>): number => {
    const n = Number(q.details?.nachtragZu?.seq);
    return Number.isInteger(n) && n > 0 ? n : 1;
};

/**
 * Plan eines Auftragsteils für ein NACHTRAGSANGEBOT, als LvPlan für Dialog
 * und Eintragen (rein).
 *  - Neuer Teil (ohne `tranche`): Zuordnung aller bisherigen Teile; was noch
 *    keine hat, bekommt den Vorschlag (proposeSupplementMapping) — denselben,
 *    den der Annahmedialog vorbelegt. Die Vorschau zeigt, was daraus wird.
 *  - Erfasster Teil (`tranche`, „Erneut eintragen"): frühere Teile plus seine
 *    eigene Zuordnung, KEIN Vorschlag — was beim Erfassen angehängt werden
 *    sollte, wird wieder angehängt, auch wenn der Vorschlag inzwischen anders
 *    lautet.
 *  - NEP-Positionen nur mit dem ersten Auftragsteil.
 * Eine fehlende Zielposition steht zusätzlich als Sperre in `packageBlocks`:
 * so sperrt der vorhandene Dialog (QuoteTrancheDialog) sie mit Grund, ohne
 * eigene Anzeige.
 */
export const supplementTranchePlan = (
    quote: Quote,
    services: ServiceItem[],
    lines: QuoteOrderLine[],
    tranche?: QuoteOrderTranche,
): LvPlan => {
    const orders = (quote.orderTranches || []).filter((t) => t && t.kind === 'order');
    const earlier = tranche ? orders.filter((t) => t.id !== tranche.id && (t.seq || 0) < (tranche.seq || 0)) : orders;
    const prior = { ...priorSupplementServiceIds(earlier), ...(tranche?.serviceIdByItemId || {}) };
    const plan = planSupplementForProjectLv(
        services,
        quote.items || [],
        lines,
        tranche ? {} : proposeSupplementMapping(services, quote.items || []),
        { seq: supplementSeqOf(quote), includeNep: earlier.length === 0, priorServiceIdByItemId: prior },
    );
    const titles = new Map((quote.items || []).map((it) => [it.id, `${it.lvPosition || it.posNumber || ''} ${it.title || ''}`.trim()]));
    return {
        ...plan,
        packageBlocks: plan.missingTargets.map((m) => ({
            itemId: m.itemId,
            message: `${titles.get(m.itemId) || 'Position'}: die zugeordnete Projektposition gibt es nicht mehr — bitte im Projekt prüfen.`,
        })),
    };
};

/** Fehlende Angebots- oder Zielpositionen sperren das Eintragen — mit Grund. */
const assertPlanUsable = (plan: LvPlan) => {
    if (plan.missing.length) {
        throw new Error('Einige Positionen des Teils stehen nicht mehr im Angebot — bitte prüfen.');
    }
    // Paketposition ueber Menge 1: gesperrt (paketErhoehungGesperrt); beim
    // Nachtrag stehen hier die fehlenden Zielpositionen (supplementTranchePlan).
    if (plan.packageBlocks.length) {
        throw new Error(plan.packageBlocks.map((b) => b.message).join(' '));
    }
};

/**
 * Darf ins Projekt-LV eingetragen werden? Wirft mit Klartext (Archiv,
 * Tochter). `was` nennt im Text, was eingetragen werden soll.
 */
export const assertProjectWritable = (project: WritableProjectLike, was: 'rest' | 'nachtrag' = 'rest') => {
    if (isProjectArchived(project)) {
        throw new Error(`Das Projekt liegt im Archiv — bitte zuerst reaktivieren, dann ${was === 'nachtrag' ? 'den Nachtrag' : 'den Rest'} eintragen.`);
    }
    const linked = project.data?.metadata?.globalData?.linkedProjectConfig;
    if (project.parent_project_id || linked?.role === 'child') {
        // Eine Tochter fuehrt ihr LV nicht selbst (es liegt bei der Mutter);
        // eine hier angehaengte Position waere fuer Buchung und Rechnung unsichtbar.
        throw new Error(
            `Das Projekt ist als Tochter mit ${linked?.linkedProjectName || 'einer Mutter'} verknüpft — `
            + (was === 'nachtrag'
                ? 'den Nachtrag bitte der Mutter zuordnen.'
                : 'die Mengen bitte über „Nachtrag & Daten-Korrektur" in der Mutter erfassen.'),
        );
    }
};

export interface ApplyTrancheOptions {
    actorName?: string;
    flatDecisions?: Partial<Record<string, FlatInclusionDecision>>;
    endDate?: string;
    orderValueCents?: number;
    /**
     * Der Auftragswert (Cent), den der Dialog beim Öffnen sah und aus dem er
     * `orderValueCents` gerechnet hat; null = es gab keinen. Gesetzt, wird
     * direkt vor dem Schreiben frisch gelesen: hat ihn inzwischen jemand
     * geändert, wird nur der Zuwachs aufgeschlagen (orderValueCentsToWrite).
     * Fehlt die Angabe, wie bisher.
     */
    orderValueBaseCents?: number | null;
    /**
     * Unmittelbar bevor saveProjectToCloud läuft. Ab hier darf ein Aufrufer
     * nichts mehr zurücknehmen — das Projekt ist womöglich schon geschrieben
     * (acceptQuoteAsSupplement: vorher Claim zurück, danach `applied: false`).
     */
    onProjectSaveAttempt?: () => void;
}

/**
 * Einen erfassten Auftragsteil ins Projekt-LV eintragen und am Angebot als
 * eingetragen markieren. Wiederholbar (siehe Kopf). Gibt das Angebot nach dem
 * Markieren zurück.
 */
export const applyTrancheToProject = async (
    quote: Quote,
    trancheId: string,
    opts: ApplyTrancheOptions = {},
): Promise<Quote> => {
    const tranche = (quote.orderTranches || []).find((t) => t.id === trancheId);
    if (!tranche || tranche.kind !== 'order') throw new Error('Auftragsteil nicht gefunden.');
    if (tranche.appliedAt) return quote;
    if (!quote.projectId) throw new Error('Am Angebot hängt kein Projekt.');

    const project = (await loadProjectById(quote.projectId)) as LoadedProject | null;
    if (!project) throw new Error('Das Projekt zum Angebot wurde nicht gefunden (gelöscht?).');

    const gd = project.data?.metadata?.globalData;
    const notes: Note[] = Array.isArray(gd?.notes) ? gd.notes : [];
    const alreadyIn = notes.some((n) => n?.id === tranche.id);
    const supplement = isSupplementQuote(quote);
    const seq = supplementSeqOf(quote);

    if (!alreadyIn) {
        assertProjectWritable(project, supplement ? 'nachtrag' : 'rest');
        const services: ServiceItem[] = Array.isArray(gd?.services) ? gd.services : [];
        const plan = supplement
            ? supplementTranchePlan(quote, services, tranche.lines, tranche)
            : planTrancheForProjectLv(services, quote.items || [], tranche.lines);
        // Paketposition ueber Menge 1 (bzw. fehlende Zielposition beim
        // Nachtrag): gesperrt. Auch beim Nachholen eines schon erfassten
        // Teils — sonst liefe ein „erneut eintragen" an der Sperre des
        // Dialogs vorbei.
        assertPlanUsable(plan);
        const pending = pendingFlatInclusionDecisions(plan.services, plan.newServiceIds, opts.flatDecisions);
        if (pending.length) {
            throw new Error('Für jede neue Position muss entschieden werden: in der Monatspauschale enthalten — ja oder nein.');
        }
        const servicesToSave = applyFlatInclusionDecisions(plan.services, opts.flatDecisions);
        const note: Note = {
            id: tranche.id,
            text: supplement
                ? supplementNoteText(quote, tranche, plan, { seq, orderNumber: quote.orderNumber })
                : trancheNoteText(quote, tranche),
            date: new Date().toISOString(),
            author: opts.actorName || tranche.byName || 'Admin',
            mentions: [],
            readBy: [],
        };
        const endDate = opts.endDate?.slice(0, 10);
        if (!gd) throw new Error('Das Projekt hat keine Leistungsdaten (globalData) — bitte im Projekt prüfen.');
        const nextData: Report = {
            ...project.data,
            metadata: {
                ...project.data.metadata,
                globalData: {
                    ...gd,
                    services: servicesToSave,
                    notes: [note, ...notes],
                    ...(endDate ? { endDate } : {}),
                },
            },
        };
        opts.onProjectSaveAttempt?.();
        await saveProjectToCloud(nextData, project.id, project.user_id);
    }

    // Auftragswert: der Dialog liefert den NEUEN Gesamtwert (bisher + Teil) —
    // ein zweiter Lauf schreibt denselben Wert, nichts addiert sich doppelt.
    //  Der Vermerk fuehrt die Kennungen ALLER eingerechneten Teile mit
    //  (orderValueNoteFor) — daran erkennt der Dialog beim erneuten Eintragen,
    //  dass ein Teil schon im Wert steckt, auch wenn danach ein weiterer kam.
    //  Mit Basis (orderValueBaseCents): frisch gelesen, ein inzwischen
    //  geänderter Wert bekommt nur den Zuwachs. Steckt der Teil laut Vermerk
    //  schon drin (zweiter Lauf), nichts nachrechnen — sonst käme der Zuwachs
    //  ein zweites Mal hinzu.
    if (opts.orderValueCents && opts.orderValueCents > 0) {
        const previous = await fetchOrderValue(quote.projectId);
        const withBase = opts.orderValueBaseCents !== undefined;
        // Mit Basis und schon eingerechnet: gar nicht schreiben — der rohe
        // Gesamtwert des Dialogs kann inzwischen veraltet sein.
        const alreadyCounted = withBase && isTrancheInOrderValue(previous?.note, tranche);
        const cents = alreadyCounted
            ? 0
            : withBase
                ? orderValueCentsToWrite(opts.orderValueCents, opts.orderValueBaseCents, previous ? previous.orderValueCents : null)
                : opts.orderValueCents;
        if (cents > 0) await fixiereAuftragswertBestEffort({
            projectId: quote.projectId,
            orderValueCents: cents,
            quoteId: quote.id,
            note: orderValueNoteFor(
                previous?.note,
                supplement
                    ? supplementOrderValueText(quote, tranche, seq)
                    : `Teilbeauftragung Teil ${tranche.seq} von ${quote.quoteNumber}: +${formatEur(tranche.netAmount)} (${tranche.orderDate}).`,
                tranche,
            ),
        });
    }

    return markAppliedOnQuote(quote.id, tranche.id);
};

/** Vermerk zum Auftragswert nach einem Nachtragsteil (ohne Kennungen, die hängt orderValueNoteFor an). */
const supplementOrderValueText = (quote: Quote, tranche: QuoteOrderTranche, seq: number): string => {
    const first = !(quote.orderTranches || []).some((t) => t.kind === 'order' && (t.seq || 0) < (tranche.seq || 0));
    return first
        ? `Nachtrag N${seq} aus ${quote.quoteNumber}: +${formatEur(tranche.netAmount)}.`
        : `Nachtrag N${seq} Teil ${tranche.seq} aus ${quote.quoteNumber}: +${formatEur(tranche.netAmount)} (${tranche.orderDate}).`;
};

/**
 * `appliedAt` am Angebot setzen. Liest die Teile frisch und schreibt
 * verankert — die Teile schreibt nur dieser Dienst, aber ein Editor-Speichern
 * (Nachfassdatum) kann dazwischen updated_at bewegen. Drei Versuche.
 */
export const markAppliedOnQuote = async (quoteId: string, trancheId: string): Promise<Quote> => {
    for (let attempt = 0; attempt < 3; attempt += 1) {
        const { data: current, error: readErr } = await supabase
            .from('quotes').select('*, customers(name)').eq('id', quoteId).maybeSingle();
        if (readErr) throw readErr;
        if (!current) throw new QuoteNotSavedError();
        const tranches: QuoteOrderTranche[] = Array.isArray(current.order_tranches) ? current.order_tranches : [];
        const target = tranches.find((t) => t.id === trancheId);
        if (!target) throw new Error('Auftragsteil am Angebot nicht mehr vorhanden.');
        if (target.appliedAt) return mapQuoteRow(current);
        const { data: rows, error } = await supabase
            .from('quotes')
            .update({
                order_tranches: markTrancheApplied(tranches, trancheId, new Date().toISOString()),
                updated_at: new Date().toISOString(),
            })
            .eq('id', quoteId)
            .eq('updated_at', current.updated_at)
            .select('*, customers(name)');
        if (error) throw error;
        if (rows?.length) return mapQuoteRow(rows[0]);
    }
    throw new Error('Das Angebot wurde mehrfach parallel gespeichert — der Teil steht im Projekt, die Markierung am Angebot bitte über „Erneut eintragen" nachholen.');
};
